/* Core & libraries */
import React, { Suspense, useEffect, useState} from 'react';
import { useTheme } from 'react-jss';
import { FormattedMessage } from 'react-intl';
import { Link } from "react-router-dom";
import moment from "helpers/moment";

/* Helpers, constants, custom methods, internal libraries */
import { useLocaleContext } from "../../contexts/localeContext";
import { useAnalytics } from "../../hooks/useAnalytics";
import { AIRLINES, PAGE_TITLES } from "../../constants/appConstants";
import { useGeneralService } from "../../services/generalService";
import { getTermsLink, getPolicyLink, getFullLanguageText } from "../../helpers/general";
import { useUserContext } from "../../contexts/userContext";
import { lazyWithPreload } from '../../helpers/lazyLoadingHelpers';
import { useFormatLocale } from '../../hooks/useFormatLocale';

/* Components */
import { ReactComponent as AALogoLarge } from '../../resources/images/aa-logo.svg';
import { ReactComponent as ASLogoLarge } from '../../resources/images/as-logo-large.svg';
import { ReactComponent as DashLogo } from "../../resources/images/dash-login-logo.svg";
import { ReactComponent as ChoiceLogoLarge } from '../../resources/images/choice-logo-large.svg';
import UCButton from "../../components/buttons"
import SpinnerBasic from '../../components/spinners/SpinnerBasic';
const CountryLanguageModalLazy = lazyWithPreload(() => import('../../components/modals/countryLanguageModal'));
import FlagIconComponent from '../../components/flagIconComponent';

/* Styles */
import styles from './styles.js';
import generalStyles from '../../resources/styles/general';


const Maintenance = () => {
  const theme = useTheme();
  const classes = styles({ theme });
  const general = generalStyles({ theme });
  const userDetails = useUserContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [maintenanceData, setMaintenanceData] = useState(false);
  const locale = useLocaleContext();
  const { locales } = useFormatLocale();

  const { initAnalytics } = useAnalytics();
  const { getMaintenanceData } = useGeneralService();

  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();

  useEffect(() => {
    (async () => {
      const data = await getMaintenanceData({
        locale: locale,
        timezone: timeZone
      });
      if (data) {
        setMaintenanceData(data)
      }
    })();
  }, [locale]);

  useEffect(() => {
      const initUtagData = {
      channel: 'content',
      pageName: PAGE_TITLES["maintenance"]
    };
    initAnalytics(initUtagData, 'init call', 'view')
  }, []);

  const handleClick = () =>{
    switch (providerCode) {
      case AIRLINES.AA:
        return window.location.href = "https://www.aa.com";
      case AIRLINES.UC:
        return window.location.href = "";
      case AIRLINES.AS:
        return window.location.href = "https://www.airalaska.com";
      case AIRLINES.AM:
        return window.location.href = "https://www.aeromexico.com";
      case AIRLINES.CHOICE:
        return window.location.href = "https://www.choicehotels.com";
    }
  }

  const getLogo = () =>{
    switch (providerCode) {
      case AIRLINES.AA:
        return <AALogoLarge/>
      case AIRLINES.UC:
        return <DashLogo/>
      case AIRLINES.AS:
        return <ASLogoLarge/>
      case AIRLINES.CHOICE:
        return <ChoiceLogoLarge/>
      case AIRLINES.AM:
        return 
    }
  }

  return (
    <div className={`${general.main} ${classes.leaveCompanyPage}`}>
      {userDetails?.userInfo?.providerInfo?.providerCode !== 'AS' && (
        <div className={`${general.main} ${classes.lang}`}>
          <div
            tabIndex="0"
            onClick={() => setShowLanguageModal(true)}
            className={classes.overlayLink}
            data-testid={'langselector'}
          >
            <FlagIconComponent locale={locale} flagClassName={classes.flagStyle}/>
            <div>{getFullLanguageText(locale)}</div>
          </div>
        </div>
      )}
      <div className={classes.largeLogo}>{getLogo()}</div>
      <div className={classes.container}>
        <div className={classes.textContainer}>
          <h1 className={classes.title} data-testid={'maintenanceTitle'}>
            {maintenanceData?.title}
          </h1>
          <p className={classes.description} data-testid={'maintenanceMessage'}>
            {maintenanceData?.message}
          </p>
          <p className={classes.time} data-testid={'maintenanceEndTime'}>
            {maintenanceData?.endDateTime && (
              <FormattedMessage
                id="maintenance.endTime"
                values={{
                  date: `${moment(maintenanceData?.endDateTime).locale(locale).format('MMM DD, YYYY')}`,
                  time: `${moment(maintenanceData?.endDateTime).format('hh:mm A')}`,
                }}
              />
            )}
          </p>
        </div>
        {
          providerCode !==AIRLINES.CHOICE &&
          <UCButton
            type="primary"
            className={classes.button}
            onClick={() => handleClick()}
            text={<FormattedMessage id={`maintenance.button.${providerCode}`} />}
            spinner={{
            name: 'maintenanceSpinner',
            size: 20,
            }}
          />
        }
      </div>
      <div className={classes.footer}>
        <div className={classes.copyright}>
          <FormattedMessage id={`footer.copyright.${providerCode}`} values={{ year: moment().format('YYYY') }} />
        </div>
        <div className={classes.links}>
          <div className={classes.terms}>
            <Link to={getTermsLink(providerCode)} target="_blank" data-testid={'termslink'}>
              <div>
                <FormattedMessage id="maintenance.terms" />
              </div>
            </Link>
          </div>
          <div className={classes.terms}>
            <Link to={getPolicyLink(providerCode)} target="_blank" data-testid={'privacypolicylink'}>
              <div>
                <FormattedMessage id="maintenance.privacy" />
              </div>
            </Link>
          </div>
        </div>
      </div>
      <Suspense fallback={showLanguageModal ? <SpinnerBasic /> : null}>
        {showLanguageModal && (
          <CountryLanguageModalLazy showModal={showLanguageModal} setShowModal={setShowLanguageModal} locales={locales}/>
        )}
      </Suspense>
    </div>
  );
};

export default Maintenance;
